/**
 * Subscription Screen Router
 *
 * Routes to the appropriate subscription screen based on platform:
 * - iOS/Android: RevenueCatSubscriptionScreen (uses native StoreKit / Google Play billing)
 * - Web: SubscriptionPlansScreen (uses web payment options)
 *
 * This ensures compliance with App Store guidelines requiring native IAP on iOS.
 */

import { useNavigation } from '@react-navigation/native';
import React, { useEffect } from 'react';
import { Platform } from 'react-native';

import { logAnalyticsEvent } from '../../../lib/firebase';

import { RevenueCatSubscriptionScreen } from './RevenueCatSubscriptionScreen';
import { SubscriptionPlansScreen } from './SubscriptionPlansScreen';

interface SubscriptionScreenRouterProps {
  route?: {
    params?: {
      preselectedPlan?: string;
      preselectedProductId?: string;
      resumeAfterAuth?: boolean;
      fromGuestUpgrade?: boolean;
      featureType?: 'multiplayer';
    };
  };
  onSubscriptionSuccess?: () => void;
}

export const shouldUseNativeStoreBilling = (platformOS: string): boolean =>
  platformOS === 'ios' || platformOS === 'android';

export const SubscriptionScreenRouter: React.FC<SubscriptionScreenRouterProps> = ({
  route,
  onSubscriptionSuccess
}) => {
  const navigation = useNavigation();

  // Every paywall presentation, native or web, goes through this router, so one
  // event here covers all variants. Params carry only the entry context.
  useEffect(() => {
    void logAnalyticsEvent('paywall_view', {
      platform: Platform.OS,
      feature_type: route?.params?.featureType ?? 'general',
      from_guest_upgrade: route?.params?.fromGuestUpgrade ?? false,
    });
    // Fire once per paywall mount, not on param changes.
  }, []);

  const handleClose = () => {
    if (navigation.canGoBack()) {
      navigation.goBack();
    }
  };

  const handleSuccess = () => {
    onSubscriptionSuccess?.();
    handleClose();
  };

  // Mobile platforms must use native store billing.
  if (shouldUseNativeStoreBilling(Platform.OS)) {
    return (
      <RevenueCatSubscriptionScreen
        route={route}
        onSuccess={handleSuccess}
        onClose={handleClose}
      />
    );
  }

  // Web keeps the hosted checkout flow.
  return (
    <SubscriptionPlansScreen
      route={route}
      onSubscriptionSuccess={handleSuccess}
    />
  );
};

export default SubscriptionScreenRouter;
